/**
 * SENTRY ON PATIENT-WEB (APP-MONITORING M1.1/M1.5; ADR-0024 decision 8/DEC-3).
 *
 * Mirrors apps/physio-app/src/monitoring/sentry.ts's three commitments — see
 * that file's header for the full reasoning. Restated briefly here:
 *
 * 1. ABSENT IS A FIRST-CLASS STATE (M0.7). No DSN → `init` never runs and
 *    every capture is a no-op — the state local dev and every existing build
 *    are already in.
 * 2. THE SCRUBBER IS THE FIRST FILTER (ADR-0024 decision 2), in `beforeSend`
 *    and `beforeBreadcrumb`, using the same shared allow-list physio-app and
 *    the API tunnel use — packages/shared/src/crashEvent.ts.
 * 3. EVENTS GO THROUGH MUMBAI (DEC-3), via this instance's own tunnel route.
 *    Unlike physio-app, there is no native-crash exception here: patient-web
 *    is web-only, so this is the ONLY path an event can take.
 *
 * ONE REAL DIFFERENCE FROM PHYSIO-APP: init is DEFERRED, not eager.
 * physio-app knows its DSN and its tunnel route at launch (both static
 * build config). Patient-web's tunnel route is
 * `/api/link/:linkToken/monitoring/tunnel` — it needs the link token, which
 * this app only learns after parsing its own URL. So `initPatientSentry`
 * takes the token as a parameter and is called once, from App.tsx's first
 * effect, not from main.tsx.
 */

import { crashTagName, scrub, scrubCrashBreadcrumb, scrubCrashEvent } from '@advaitnetra/shared';
import * as Sentry from '@sentry/browser';
import type { BrowserOptions } from '@sentry/browser';

import { API_BASE_URL, SENTRY_DSN } from '../config/env';
import { telemetryInstance } from './instanceSource';

const TUNNEL_PATH_PREFIX = '/api/link/';
const TUNNEL_PATH_SUFFIX = '/monitoring/tunnel';
const ENVELOPE_CONTENT_TYPE = 'application/x-sentry-envelope';

let enabled = false;
/** Set once, by initPatientSentry — the link token this run's tunnel targets. */
let tunnelPath = '';

/**
 * Post one serialized envelope through the API. Never throws — a tunnel that
 * is unreachable must be indistinguishable from monitoring being off (M0.7).
 * No Authorization header: patient-web never sends one, for anyone (api.ts's
 * own rule) — the link token in the tunnel path IS this request's capability.
 */
async function postEnvelope(body: string): Promise<{ statusCode: number }> {
  try {
    const res = await fetch(`${API_BASE_URL.replace(/\/$/, '')}${tunnelPath}`, {
      method: 'POST',
      headers: { 'content-type': ENVELOPE_CONTENT_TYPE },
      body,
    });
    return { statusCode: res.status };
  } catch {
    // Offline or DNS failure. Nothing was sent and nothing is kept — the same
    // known gap physio-app's own transport discloses (ADR-0024 §7 / M1.8).
    return { statusCode: 0 };
  }
}

/**
 * An envelope, as bytes on the wire — the same hand-rolled protocol encoder
 * as apps/physio-app/src/monitoring/sentry.ts's `serializeEnvelope`,
 * duplicated rather than imported: each SDK package (`@sentry/react-native`,
 * `@sentry/browser`) hoists its own nested `@sentry/core`, so a shared helper
 * typed against either package's `Envelope` would hit the same dual-package
 * mismatch that file's own comment describes. Restating the protocol locally
 * — a JSON header line, then a header/payload line pair per item,
 * newline-delimited — sidesteps it in both places at once.
 */
const NL = '\n';
type SentryEnvelope = [Record<string, unknown>, Array<[Record<string, unknown>, unknown]>];

/** UTF-8 byte length, computed by hand for the same reason as physio-app's copy. */
function byteLength(value: string): number {
  let bytes = 0;
  for (const ch of value) {
    const cp = ch.codePointAt(0) ?? 0;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
}

export function serializeEnvelope(envelope: SentryEnvelope): string {
  const [headers, items] = envelope;
  let out = JSON.stringify(headers) + NL;
  for (const [itemHeaders, payload] of items) {
    const body = typeof payload === 'string' ? payload : JSON.stringify(payload);
    out += JSON.stringify({ ...itemHeaders, length: byteLength(body) }) + NL + body + NL;
  }
  return out;
}

/**
 * The transport. Flush waits for in-flight sends for the same reason
 * physio-app's does: the SDK's fatal-error handler calls
 * `client.flush(timeout)` and must not tear the page down before the POST
 * leaves the browser. STILL NO OFFLINE QUEUE — a failed send is discarded,
 * not cached (the same disclosed ADR-0024 §7 gap).
 */
const tunnelTransport: NonNullable<BrowserOptions['transport']> = () => {
  const inFlight = new Set<Promise<unknown>>();

  return {
    send: (envelope) => {
      const sending = postEnvelope(serializeEnvelope(envelope as unknown as SentryEnvelope));
      inFlight.add(sending);
      void sending.finally(() => inFlight.delete(sending));
      return sending;
    },
    flush: async (timeout) => {
      if (inFlight.size === 0) return true;
      const settled = Promise.allSettled([...inFlight]).then(() => true);
      if (timeout === undefined) return settled;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const expired = new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), timeout);
      });
      return Promise.race([settled, expired]).finally(() => {
        if (timer !== undefined) clearTimeout(timer);
      });
    },
  };
};

/**
 * Initialise, once the link token is known — see this file's header for why
 * this is deferred rather than called at module load. Returns whether
 * monitoring is live, same contract as physio-app's `initAppSentry`.
 */
export function initPatientSentry(linkToken: string): boolean {
  if (!SENTRY_DSN) {
    enabled = false;
    return false;
  }
  tunnelPath = `${TUNNEL_PATH_PREFIX}${encodeURIComponent(linkToken)}${TUNNEL_PATH_SUFFIX}`;

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: telemetryInstance(),
      release: import.meta.env['VITE_RELEASE'] as string | undefined,
      // M1.4 / ADR-0024 decision 2 — everything an event carries is something
      // this codebase chose to put there and the scrubber then let through.
      sendDefaultPii: false,
      // TRACING OFF BY OMISSION, not by zero — same reasoning as physio-app's
      // sentry.ts: leaving the key unset is the setting that actually means
      // "off", where 0 would still install the performance/request stack.
      //
      // Session replay and the feedback widget are not added as integrations
      // below, which is how they stay off — neither is imported here.
      integrations: (defaults) =>
        // Keep the browser SDK's default error-catching integrations
        // (window.onerror, unhandledrejection, fetch/xhr instrumentation,
        // linked-error chaining) — unlike physio-app, there is no separate
        // native crash path here, so these ARE the only way an uncaught
        // patient-web error is ever seen. Swap only the console-breadcrumbs
        // integration for one with console breadcrumbs off: this app has no
        // logger writing PHI-risk lines through console.* the way physio-app
        // flagged, but scrubCrashBreadcrumb only applies value rules to a
        // breadcrumb's message, not a full removal, so leaving an
        // uncontrolled console feed on is the same risk physio-app's own
        // 2026-09-08 fix closed.
        defaults
          .filter((integration) => integration.name !== 'Breadcrumbs')
          .concat(Sentry.breadcrumbsIntegration({ console: false })),
      transport: tunnelTransport,
      // Payload maps only, never the envelope (see crashEvent.ts's own
      // header) — this is what stops the mid-call alert firing, because
      // `in_call`'s patient-web analogue would be a tag too.
      beforeSend(event) {
        event.environment = telemetryInstance();
        return scrubCrashEvent(event);
      },
      beforeBreadcrumb(breadcrumb) {
        const url = breadcrumb.data?.['url'];
        if (typeof url === 'string' && url.includes(TUNNEL_PATH_SUFFIX)) return null;
        return scrubCrashBreadcrumb(breadcrumb);
      },
    });
    enabled = true;
    return true;
  } catch {
    // A crash reporter that crashes the app at launch is the worst possible
    // outcome here. Initialisation failure degrades to "off" (M0.7).
    enabled = false;
    return false;
  }
}

/**
 * Forward one captured error to Sentry, with allow-listed tags. Never throws
 * and never awaits: telemetry.captureError calls this from a render-phase
 * error boundary, where a second failure would take the whole page down with
 * no boundary left to catch it.
 */
export function forwardToSentry(
  error: unknown,
  context: Record<string, unknown>,
  eventId: string,
): void {
  if (!enabled) return;
  try {
    Sentry.withScope((scope) => {
      const safe = scrub({ ...context, instance: telemetryInstance() }, 'crash');
      if (safe && typeof safe === 'object') {
        for (const [key, value] of Object.entries(safe as Record<string, unknown>)) {
          if (value !== undefined) scope.setTag(crashTagName(key), String(value));
        }
      }
      scope.setTag('appEventId', eventId);
      Sentry.captureException(error);
    });
  } catch {
    // Monitoring never blocks and never escalates (M0.7).
  }
}
